import { useEffect } from "react";

import { FileDropper, FileDropperResponse, Lang } from "@noli/core";

import { toast, useOwnToast } from "@/utils/misc";
import { SafeOp } from "@/utils/safeOp";
import { translate, useIsReady } from "@noli/business";
import { setDropping, statusStore } from "@/stores/status";
import { Actions } from "@/utils/api";
import { Editor_Words } from "@/utils/lang/editor";

function useFileDropper(lang: Lang, containerId: string): void {
  function onDrop(): void {
    setDropping(true);
    setTimeout(() => {
      if (statusStore.dropping) {
        toast(t, "info", translate(Editor_Words["dropping-message"], lang));
      }
    }, dropPatience);
  }

  async function failed(): Promise<void> {
    toast(t, "error", translate(Editor_Words["upload-image-error-message"], lang));
  }

  type UploadResponse = { success: boolean; reason: "none" | "unknown" | "type" | "upload" };
  async function onSuccessOne(res: FileDropperResponse): Promise<UploadResponse> {
    if (res.type !== "success" || !res.data) return { success: false, reason: "unknown" };
    const { type, source } = res.data;
    if (type !== "png" && type !== "jpeg") {
      return { success: false, reason: "type" };
    }

    const file = new File([source], `image.${type}`, { type });
    const uploadRes = await SafeOp.uploadImage(t, lang, file, {
      failed: async () => void 0,
      returnWH: true,
    });
    if (!uploadRes) {
      return { success: false, reason: "upload" };
    }
    const { cdn } = uploadRes.urls;
    Actions.importMeta({
      t,
      lang,
      type: "upload",
      meta: {
        type: "upload",
        data: {
          url: cdn,
          wh: uploadRes.wh,
          isDrag: true,
        },
      },
      setupInfo: {
        checkBusy: true,
        busyAliases: [".upload."],
      },
      opt: null,
    });
    return { success: true, reason: "none" };
  }
  async function onSuccess(resList: FileDropperResponse[]): Promise<void> {
    setDropping(false);
    if (resList.length === 0) return;
    toast(t, "success", translate(Editor_Words["uploading-message"], lang));
    const uploadResList = await Promise.all(resList.map((res) => onSuccessOne(res)));
    if (uploadResList.some((res) => res.reason === "type")) {
      toast(t, "error", translate(Editor_Words["strange-image-error-message"], lang));
    }
    if (uploadResList.some((res) => res.reason === "upload")) {
      failed();
    }
  }

  async function onError(resList: FileDropperResponse[]): Promise<void> {
    setDropping(false);
    console.log("fileDropper.error", resList);
    failed();
    if (resList.length === 0) return;
    resList.forEach((res) => {
      if (res.reason) {
        toast(t, "error", translate(res.reason, lang));
      }
    });
  }

  const t = useOwnToast();
  const dropPatience = 500;

  useEffect(() => {
    const dropper = new FileDropper({
      onDrop,
      onSuccess,
      onError,
      urlType: "dataUrl",
      listenTarget: document.getElementById(containerId) as HTMLElement,
      bypassWHCheck: true,
    });
    dropper.init();
    return () => {
      dropper.destroy();
    };
  }, [useIsReady(), lang]);
}

export default useFileDropper;
